/**
 * Single tenant detail: full stats and edit form.
 * Route: /tenants/:id or /tenants/new
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  FormControlLabel,
  Switch,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { tenantService } from '../services/api';

function formatBytes(bytes) {
  if (bytes == null || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

export default function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [userForm, setUserForm] = useState({ username: '', password: '', role: 'admin' });
  const [creatingUser, setCreatingUser] = useState(false);
  const [userSuccess, setUserSuccess] = useState('');

  /** Form state for create/edit */
  const [form, setForm] = useState({
    company_code: '',
    name: '',
    state: 'active',
    contact_email: '',
    contact_phone: '',
    gallery_space_limit: '',
    is_active: true,
  });

  useEffect(() => {
    if (isNew) {
      setForm((f) => ({ ...f, company_code: '', name: '', state: 'active', is_active: true }));
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await tenantService.getById(id);
        if (!cancelled) {
          setTenant(res.data);
          setForm({
            company_code: res.data.company_code || '',
            name: res.data.name || '',
            state: res.data.state || 'active',
            contact_email: res.data.contact_email || '',
            contact_phone: res.data.contact_phone || '',
            gallery_space_limit: res.data.gallery_space_limit != null ? String(res.data.gallery_space_limit) : '',
            is_active: res.data.is_active !== false,
          });
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load tenant');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isNew]);

  const handleChange = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleCreateUser = async () => {
    if (!tenant || !userForm.username.trim() || !userForm.password) {
      setError('Username and password are required');
      return;
    }
    if (userForm.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setError('');
    setUserSuccess('');
    setCreatingUser(true);
    try {
      await tenantService.createUser(tenant._id, {
        username: userForm.username.trim(),
        password: userForm.password,
        role: userForm.role,
      });
      setUserForm({ username: '', password: '', role: 'admin' });
      setUserSuccess('User created. They can sign in with company code ' + tenant.company_code + ' and this username/password.');
      const res = await tenantService.getById(tenant._id);
      setTenant(res.data);
    } catch (err) {
      setError(err.message || 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleSave = async () => {
    setError('');
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    if (isNew) {
      if (!form.company_code.trim()) {
        setError('Company code is required');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        state: form.state,
        contact_email: form.contact_email.trim() || undefined,
        contact_phone: form.contact_phone.trim() || undefined,
        gallery_space_limit: form.gallery_space_limit ? Number(form.gallery_space_limit) : 0,
        is_active: form.is_active,
      };
      if (isNew) {
        payload.company_code = form.company_code.trim().toUpperCase();
        const res = await tenantService.create(payload);
        navigate(`/tenants/${res.data._id}`, { replace: true });
      } else {
        await tenantService.update(id, payload);
        const res = await tenantService.getById(id);
        setTenant(res.data);
      }
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !isNew) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mb: 2 }}>
        Back to list
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* How company users sign in (existing tenant only) */}
        {!isNew && tenant && (
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ bgcolor: 'grey.50', borderColor: 'primary.light' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary.main">
                  How company users sign in
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Share the <strong>company code</strong> with the customer. They use it to sign in to the Dashboard (web) or the mobile app.
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Company code: <strong>{tenant.company_code}</strong>
                </Typography>
                <Typography component="ol" variant="body2" sx={{ pl: 2, mt: 1, mb: 0 }}>
                  <li>First user: sign up in the <strong>mobile app</strong> (Sign Up) with this company code + username + password.</li>
                  <li>Then anyone can sign in to the <strong>Dashboard</strong> or <strong>mobile app</strong> with: Company code + Username + Password.</li>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Stats (only when existing tenant) */}
        {!isNew && tenant && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Stats
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography color="text.secondary">Users</Typography>
                    <Typography variant="h6">{tenant.users_count ?? 0}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography color="text.secondary">Installations</Typography>
                    <Typography variant="h6">{tenant.installations_count ?? 0}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography color="text.secondary">Teams</Typography>
                    <Typography variant="h6">{tenant.teams_count ?? 0}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography color="text.secondary">Installation items</Typography>
                    <Typography variant="h6">{tenant.installation_items_count ?? 0}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography color="text.secondary">Devices</Typography>
                    <Typography variant="h6">{tenant.installation_devices_count ?? 0}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography color="text.secondary">Gallery space</Typography>
                    <Typography variant="h6">
                      {formatBytes(tenant.gallery_space_used ?? 0)}
                      {(tenant.gallery_space_limit ?? 0) > 0 && (
                        <> / {formatBytes(tenant.gallery_space_limit)}</>
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Edit tenant form - side by side with Create company user when existing tenant */}
        <Grid item xs={12} md={isNew ? 12 : 6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {isNew ? 'New tenant' : 'Edit tenant'}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <TextField
                fullWidth
                label="Company code"
                value={form.company_code}
                onChange={(e) => handleChange('company_code', e.target.value.toUpperCase())}
                disabled={!isNew}
                helperText={!isNew ? 'Cannot change after creation' : ''}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                required
                label="Name"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Contact email"
                type="email"
                value={form.contact_email}
                onChange={(e) => handleChange('contact_email', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Contact phone"
                value={form.contact_phone}
                onChange={(e) => handleChange('contact_phone', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Gallery space limit (bytes, 0 = unlimited)"
                type="number"
                inputProps={{ min: 0 }}
                value={form.gallery_space_limit}
                onChange={(e) => handleChange('gallery_space_limit', e.target.value)}
                sx={{ mb: 2 }}
              />
              {!isNew && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>State</InputLabel>
                  <Select
                    label="State"
                    value={form.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                  </Select>
                </FormControl>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                  />
                }
                label="Active"
              />
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {isNew ? 'Create' : 'Save'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Create company user - side by side with Edit tenant when existing tenant */}
        {!isNew && tenant && (
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Create company user
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Set the first (or another) username and password for this company. They sign in with company code <strong>{tenant.company_code}</strong> and these credentials.
                </Typography>
                {userSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUserSuccess('')}>
                    {userSuccess}
                  </Alert>
                )}
                <TextField
                  fullWidth
                  size="small"
                  label="Username"
                  value={userForm.username}
                  onChange={(e) => setUserForm((f) => ({ ...f, username: e.target.value }))}
                  disabled={creatingUser}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Password (min 6 characters)"
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))}
                  disabled={creatingUser}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    label="Role"
                    value={userForm.role}
                    onChange={(e) => setUserForm((f) => ({ ...f, role: e.target.value }))}
                  >
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="worker">Worker</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={creatingUser || !userForm.username.trim() || userForm.password.length < 6}
                  onClick={handleCreateUser}
                >
                  {creatingUser ? <CircularProgress size={24} /> : 'Create user'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
